/**
 * Users — see who has access, and change it.
 *
 * WHAT THIS PAGE IS FOR, in order: (1) find a person, (2) see what they can
 * open, (3) change it. The previous version made you read stored role strings
 * ('crm:csm') to answer (2) and offered a separate exact-email form to do (1).
 * Now the roster shows page access in plain language and one search box does both
 * jobs — it filters the loaded roster as you type, and offers the exact-email
 * lookup as a fallback when nothing matches (that lookup is the only path to a
 * deliberately-hidden system/test account, #1377).
 *
 * TWO THINGS THIS PAGE MUST NOT DO, both learned from staging:
 *   - It must not render a failed role read as "no roles". `roles: null` from the
 *     server means unavailable; the roster says "access unknown" and the banner
 *     explains. Showing an authoritative-looking empty state over a broken query
 *     is worse than showing an error.
 *   - It must not omit people who can log in. The roster is the union of the
 *     directory and everyone who has ever authenticated, and anyone present only
 *     because they signed in is flagged rather than silently dropped.
 *
 * Gating: mounts under <AdminGuard> (fail-closed, server-side GET
 * /api/admin/me). Every call goes through the data-access hooks → vendored `api`
 * wrapper → the single auth resolver seam. No direct fetch, no per-page MSAL.
 */
import { type FormEvent, useMemo, useState } from 'react';
import { Button, cn } from '@andavo/web-ui';
import { useMediaQuery } from '@/features/chat/hooks/useMediaQuery';
import {
  AccessChips,
  AccessPageList,
  SourceBadge,
  rolesUnavailable,
} from '../../components/AccessSummary';
import { InvitePanel } from '../../components/InvitePanel';
import { PageAccessEditor } from '../../components/PageAccessEditor';
import {
  AdminEmpty,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Panel,
  Table,
  Td,
  Th,
  Tr,
} from '../../components/primitives';
import { RoleSetEditor } from '../../components/RoleSetEditor';
import { useAdminUserByEmail, useAdminUsers } from '../../data-access/useAdmin';
import { absTime } from '../../lib/format';
import type { AdminUser } from '../../types';

/**
 * A readable name for the row. Rows known only from auth.users carry no directory
 * displayName, and rendering "—" as the person's name made the roster look broken
 * ("who is this?"). Derive something human from the local part instead —
 * `jeanine.eissler@` → "Jeanine Eissler" — and mark it as derived via `title` so
 * nobody mistakes it for a real directory name.
 */
function displayName(user: AdminUser): string {
  if (user.name) return user.name;
  const local = (user.email || '').split('@')[0] ?? '';
  if (!local) return '—';
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Invite-lifecycle badge: has the invited user redeemed + signed in yet? */
function InviteStatus({ user }: { user: AdminUser }) {
  if (user.invite_state === 'accepted') {
    return (
      <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-inter font-medium text-emerald-700 text-xs">
        Accepted
      </span>
    );
  }
  if (user.invite_state === 'pending') {
    return (
      <span className="rounded-full bg-amber-500/10 px-2 py-0.5 font-inter font-medium text-amber-700 text-xs">
        Invited
      </span>
    );
  }
  // No invite lifecycle (a direct member, or a user we only know from
  // auth.users). Having signed in is the more useful fact — say that instead of
  // an em-dash that reads as "something is missing".
  if (user.has_signed_in) {
    return (
      <span className="font-inter text-content-tertiary text-xs">
        Signed in
      </span>
    );
  }
  return <span className="text-content-tertiary text-xs">—</span>;
}

function UserRow({
  user,
  isExpanded,
  isEditing,
  onToggleExpand,
  onEdit,
}: {
  user: AdminUser;
  isExpanded: boolean;
  isEditing: boolean;
  onToggleExpand: (user: AdminUser) => void;
  onEdit: (user: AdminUser) => void;
}) {
  return (
    <>
      <Tr className={isEditing ? 'bg-surface-action-subtle/40' : undefined}>
        <Td>
          <div className="flex flex-col gap-0.5">
            <span className="text-content-primary">{displayName(user)}</span>
            {/* data-user-email is the stable hook the e2e uses to target a
                SPECIFIC user's row (the dedicated test account) — so a test never
                edits a real human's access by row-ordering accident. */}
            <span
              className="font-inter text-content-secondary text-xs"
              data-testid="user-row-email"
              data-user-email={user.email}
            >
              {user.email}
            </span>
            <SourceBadge user={user} />
          </div>
        </Td>
        <Td>
          <AccessChips user={user} />
        </Td>
        <Td>
          <button
            aria-expanded={isExpanded}
            className="font-inter text-content-action text-xs hover:underline"
            data-testid="user-toggle-pages"
            onClick={() => onToggleExpand(user)}
            type="button"
          >
            {isExpanded ? 'Hide pages' : 'Show pages'}
          </button>
        </Td>
        <Td className="text-content-tertiary text-xs">
          {user.last_active_at ? absTime(user.last_active_at) : '—'}
        </Td>
        <Td>
          <InviteStatus user={user} />
        </Td>
        <Td>
          <Button
            data-testid="user-edit-access"
            onClick={() => onEdit(user)}
            size="sm"
            type="button"
            variant={isEditing ? 'secondary' : 'primary'}
          >
            {isEditing ? 'Editing' : 'Change access'}
          </Button>
        </Td>
      </Tr>
      {isExpanded ? (
        <Tr>
          {/* Indent the detail under the Person column so the page list reads as
              belonging to the row above it. */}
          <Td className="bg-surface-background">{null}</Td>
          <Td className="bg-surface-background" colSpan={5}>
            <AccessPageList user={user} />
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

/**
 * MOBILE ROSTER CARD — the same row, restacked for a phone.
 *
 * The roster is a 6-column table (Person / Can access / Pages / Last active /
 * Status / Actions). Wrapped in overflow-x-auto it does not BREAK on a 390px
 * screen, but "Change access" — the button you came for — sits off the right edge
 * behind a sideways scroll. Not broken is not the same as usable.
 *
 * Below `sm` each person becomes a card: identity, then what they can reach, then
 * one full-width action. Above `sm` this is `hidden` and the table renders exactly
 * as before — the desktop layout is deliberately untouched, not re-derived.
 *
 * Its testids are DISTINCT from the table's (`user-card-*` vs `user-row-*`).
 * Tailwind hides by CSS, so in jsdom BOTH trees exist; sharing testids would make
 * every existing row query ambiguous and quietly match the wrong element.
 */
function UserCard({
  user,
  isExpanded,
  isEditing,
  onToggleExpand,
  onEdit,
}: {
  user: AdminUser;
  isExpanded: boolean;
  isEditing: boolean;
  onToggleExpand: (user: AdminUser) => void;
  onEdit: (user: AdminUser) => void;
}) {
  return (
    <li
      className={cn(
        'rounded-md border border-border-subtle p-3',
        isEditing ? 'bg-surface-action-subtle/40' : 'bg-surface-foreground',
      )}
      data-testid="user-card"
      data-user-email={user.email}
    >
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-content-primary text-sm">
          {displayName(user)}
        </span>
        <span
          className="break-all font-inter text-content-secondary text-xs"
          data-testid="user-card-email"
        >
          {user.email}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <AccessChips user={user} />
        <SourceBadge user={user} />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-inter text-content-tertiary text-xs">
        <InviteStatus user={user} />
        <span>
          {user.last_active_at ? absTime(user.last_active_at) : 'never active'}
        </span>
        <button
          aria-expanded={isExpanded}
          className="text-content-action hover:underline"
          data-testid="user-card-toggle-pages"
          onClick={() => onToggleExpand(user)}
          type="button"
        >
          {isExpanded ? 'Hide pages' : 'Show pages'}
        </button>
      </div>

      {isExpanded ? (
        <div className="mt-2 rounded border border-border-subtle bg-surface-background p-2">
          <AccessPageList user={user} />
        </div>
      ) : null}

      {/* Full-width so it is a comfortable tap target rather than a 90px button
          in the corner of a 390px screen. */}
      <Button
        className="mt-3 w-full"
        data-testid="user-card-edit-access"
        onClick={() => onEdit(user)}
        size="sm"
        type="button"
        variant={isEditing ? 'secondary' : 'primary'}
      >
        {isEditing ? 'Editing' : 'Change access'}
      </Button>
    </li>
  );
}

const ROSTER_HEAD = (
  <>
    <Th>Person</Th>
    <Th>Can access</Th>
    <Th>Pages</Th>
    <Th>Last active</Th>
    <Th>Status</Th>
    <Th>Actions</Th>
  </>
);

/**
 * The banner that stops this page from lying. When the server reports
 * `roles: null` for everyone, the roster genuinely does not know who has what —
 * so say it once, loudly, at the top, and name the likely cause. This is the
 * screen where a silent failure turns into a wrong access decision.
 */
function RolesUnavailableBanner() {
  return (
    <div
      className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 font-inter text-amber-800 text-sm"
      data-testid="roles-unavailable-banner"
      role="alert"
    >
      <strong className="font-medium">Access data is unavailable.</strong> The
      server could not read the role table, so every row below shows “access
      unknown” — that is not the same as nobody having access. Usually the
      SECURITY DEFINER helper <code>public.admin_list_user_roles()</code> is
      missing from this environment’s database (migration 079). Don’t re-grant
      access from this screen until it resolves.
    </div>
  );
}

export function UsersPage() {
  const { data, isPending, isError, error, refetch, isFetching } =
    useAdminUsers();
  const users = useMemo(() => data ?? [], [data]);

  const [query, setQuery] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingSnapshot, setEditingSnapshot] = useState<AdminUser | null>(
    null,
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Below Tailwind's `sm` (640px) the 6-column roster forces a sideways scroll to
  // reach "Change access" — the button you opened the page for.
  const isCompact = useMediaQuery('(max-width: 639px)');

  const lookup = useAdminUserByEmail(lookupEmail);
  const lookupResults = lookup.data ?? [];

  // Live filter over the loaded roster — name or email, case-insensitive. This
  // is the common case (find a colleague); the server lookup below is the
  // fallback for accounts the roster deliberately hides.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.name ?? '').toLowerCase().includes(q),
    );
  }, [users, query]);

  // The whole roster reporting null = the role read failed, not a coincidence of
  // empty grants. Require at least one row so an empty roster doesn't trip it.
  const accessDataBroken =
    users.length > 0 && users.every((u) => rolesUnavailable(u));

  // Resolve the row being edited from the freshest data when available (so a
  // refetch after save flows through), else the snapshot captured at click time
  // (covers editing a lookup-only result that isn't in the roster).
  const editingUser = useMemo<AdminUser | null>(() => {
    if (!editingId) return null;
    return (
      users.find((u) => u.id === editingId) ??
      lookupResults.find((u) => u.id === editingId) ??
      (editingSnapshot?.id === editingId ? editingSnapshot : null)
    );
  }, [editingId, users, lookupResults, editingSnapshot]);

  const handleEdit = (user: AdminUser) => {
    setEditingId((prev) => (prev === user.id ? null : user.id));
    setEditingSnapshot(user);
  };

  const handleToggleExpand = (user: AdminUser) => {
    setExpandedId((prev) => (prev === user.id ? null : user.id));
  };

  const onLookupSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLookupEmail(query.trim().toLowerCase());
  };

  /**
   * Cards on a phone, the existing table from `sm` up — ONE of them, never both.
   *
   * Rendering both and hiding one with `sm:hidden` was the obvious approach and
   * the wrong one: CSS hides visually but the nodes still exist, so a screen
   * reader announces the entire roster TWICE. (It also made every text query in
   * the tests ambiguous, which is how the duplication showed up at all.)
   *
   * `useMediaQuery` returns false when matchMedia is unavailable, so SSR and
   * jsdom both get the table — the desktop layout stays the default and the
   * existing specs keep asserting against it.
   */
  const renderRoster = (rows: AdminUser[]) =>
    isCompact ? (
      <ul className="flex flex-col gap-2" data-testid="user-cards">
        {rows.map((user) => (
          <UserCard
            isEditing={user.id === editingId}
            isExpanded={user.id === expandedId}
            key={user.id}
            onEdit={handleEdit}
            onToggleExpand={handleToggleExpand}
            user={user}
          />
        ))}
      </ul>
    ) : (
      <Table head={ROSTER_HEAD}>{renderRows(rows)}</Table>
    );

  const renderRows = (rows: AdminUser[]) =>
    rows.map((user) => (
      <UserRow
        isEditing={user.id === editingId}
        isExpanded={user.id === expandedId}
        key={user.id}
        onEdit={handleEdit}
        onToggleExpand={handleToggleExpand}
        user={user}
      />
    ));

  return (
    <div data-testid="users-page">
      <AdminPageHeader
        actions={
          <button
            className="rounded-md border border-border-subtle px-3 py-1.5 font-inter text-content-secondary text-sm hover:bg-surface-background"
            onClick={() => void refetch()}
            type="button"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        }
        subtitle="Everyone who can sign in, and what each person can open. Change access by choosing pages."
        title="Users"
      />

      {accessDataBroken ? <RolesUnavailableBanner /> : null}

      <InvitePanel />

      <Panel>
        <form
          className="mb-4 flex flex-wrap items-end gap-2"
          data-testid="user-search-form"
          onSubmit={onLookupSubmit}
        >
          <label className="flex min-w-0 flex-col gap-1 font-inter text-content-secondary text-sm">
            Find someone
            <input
              className="w-full rounded-md border border-border-subtle bg-surface-background px-3 py-1.5 font-inter text-content-primary text-sm sm:w-80"
              data-testid="user-search-input"
              onChange={(e) => setQuery(e.target.value)}
              placeholder="name or email"
              type="text"
              value={query}
            />
          </label>
          {/* Only offered once the roster has nothing to show for the query —
              this hits the server and is the sole route to a hidden
              system/service/test account, so it stays a deliberate action. */}
          {query.trim() && filtered.length === 0 ? (
            <Button
              data-testid="user-lookup-submit"
              size="sm"
              type="submit"
              variant="secondary"
            >
              {lookup.isFetching ? 'Searching…' : 'Search all accounts'}
            </Button>
          ) : null}
        </form>

        {isPending ? <AdminLoading /> : null}
        {isError ? (
          <AdminError
            message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
          />
        ) : null}
        {data && users.length === 0 ? (
          <AdminEmpty>No users found.</AdminEmpty>
        ) : null}

        {data && filtered.length > 0 ? renderRoster(filtered) : null}

        {data && users.length > 0 && filtered.length === 0 ? (
          <AdminEmpty>
            Nobody in the roster matches “{query.trim()}”. System, service and
            test accounts are hidden — use “Search all accounts” to reach one by
            its exact full email.
          </AdminEmpty>
        ) : null}

        {lookup.isError ? (
          <div className="mt-3">
            <AdminError
              message={`Lookup failed: ${
                (lookup.error as Error)?.message ?? 'unknown'
              }`}
            />
          </div>
        ) : null}
        {lookupEmail && !lookup.isFetching && lookupResults.length === 0 ? (
          <AdminEmpty>
            No account found for “{lookupEmail}”. That search needs the exact
            full email address.
          </AdminEmpty>
        ) : null}
        {lookupResults.length > 0 ? (
          <div className="mt-4" data-testid="user-lookup-results">
            <h3 className="mb-2 font-sans font-medium text-content-primary text-sm">
              Found outside the roster
            </h3>
            {renderRoster(lookupResults)}
          </div>
        ) : null}
      </Panel>

      {editingUser ? (
        <div className="mt-4 flex flex-col gap-3">
          <PageAccessEditor
            key={editingUser.id}
            onDone={() => setEditingId(null)}
            user={editingUser}
          />

          {/* ADVANCED — the role editor, still here on purpose. Pages can't
              express everything the model can grant: Expense approver vs user,
              and the CRM write/delete capabilities, are role-level distinctions
              with no page of their own. Hiding roles entirely would have removed
              real capability, so it moved behind an expander instead. */}
          <div>
            <button
              aria-expanded={advancedOpen}
              className="flex items-center gap-1.5 font-inter text-content-secondary text-sm hover:text-content-primary"
              data-testid="advanced-roles-toggle"
              onClick={() => setAdvancedOpen((o) => !o)}
              type="button"
            >
              <span aria-hidden className="text-content-tertiary text-xs">
                {advancedOpen ? '▾' : '▸'}
              </span>
              Advanced — edit roles directly
            </button>
            {advancedOpen ? (
              <p className="mt-1 mb-2 font-inter text-content-tertiary text-xs">
                For grants that aren’t a page: Expense approver, and the
                Customer Management write/delete capabilities. Saving here
                replaces the whole role set, same as above.
              </p>
            ) : null}
            {advancedOpen ? (
              <RoleSetEditor key={editingUser.id} user={editingUser} />
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default UsersPage;
